/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { aiIntegrationsRetrySchedule } from "../funcs/aiIntegrationsRetrySchedule.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AIIntegrationScheduleState } from "../models/components/aiintegrationschedulestate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RetryAIIntegrationScheduleRequest,
  RetryAIIntegrationScheduleSecurity,
} from "../models/operations/retryaiintegrationschedule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RetryAIIntegrationScheduleMutationVariables = {
  request: RetryAIIntegrationScheduleRequest;
  security?: RetryAIIntegrationScheduleSecurity | undefined;
  options?: RequestOptions;
};

export type RetryAIIntegrationScheduleMutationData = AIIntegrationScheduleState;

export type RetryAIIntegrationScheduleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * retrySchedule aiIntegrations
 *
 * @remarks
 * Make one sync schedule due immediately, lifting any automatic pause and resetting its failure streak. The scheduler picks it up on its next tick.
 */
export function useRetryAIIntegrationScheduleMutation(
  options?: MutationHookOptions<
    RetryAIIntegrationScheduleMutationData,
    RetryAIIntegrationScheduleMutationError,
    RetryAIIntegrationScheduleMutationVariables
  >,
): UseMutationResult<
  RetryAIIntegrationScheduleMutationData,
  RetryAIIntegrationScheduleMutationError,
  RetryAIIntegrationScheduleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRetryAIIntegrationScheduleMutation(client, options),
    ...options,
  });
}

export function mutationKeyRetryAIIntegrationSchedule(): MutationKey {
  return ["@gram/client", "aiIntegrations", "retrySchedule"];
}

export function buildRetryAIIntegrationScheduleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RetryAIIntegrationScheduleMutationVariables,
  ) => Promise<RetryAIIntegrationScheduleMutationData>;
} {
  return {
    mutationKey: mutationKeyRetryAIIntegrationSchedule(),
    mutationFn: function retryAIIntegrationScheduleMutationFn({
      request,
      security,
      options,
    }): Promise<RetryAIIntegrationScheduleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(aiIntegrationsRetrySchedule(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
